import { useState, useRef, useEffect, lazy, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from 'react-router-dom';

import { BottomNav } from './components/BottomNav';
import { ErrorBoundary } from './components/ErrorBoundary';
import { RightSidebar } from './components/RightSidebar';
import { SettingsOverlay } from './components/SettingsOverlay';
import { AuthProvider } from './contexts/AuthContext';
import { DetectionProvider } from './contexts/DetectionContext';
import { MinimalHome, MinimalHomeRef } from './pages/MinimalHome';
import { initGA, trackPageView } from './utils/analytics';

// Lazy load pages
const MinimalLibrary = lazy(() =>
  import('./pages/MinimalLibrary').then((module) => ({
    default: module.MinimalLibrary,
  }))
);
const MinimalSearch = lazy(() =>
  import('./pages/MinimalSearch').then((module) => ({
    default: module.MinimalSearch,
  }))
);
const Profile = lazy(() =>
  import('./pages/Profile').then((module) => ({ default: module.Profile }))
);
const Settings = lazy(() =>
  import('./pages/Settings').then((module) => ({ default: module.Settings }))
);

function AppContent() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const homeRef = useRef<MinimalHomeRef | null>(null);
  const location = useLocation();

  const handleStartDetection = () => {
    if (homeRef.current) {
      homeRef.current.startDetection();
    }
  };

  const handleStopDetection = () => {
    if (homeRef.current) {
      homeRef.current.stopDetection();
    }
  };

  // Initialize Google Analytics on app load
  useEffect(() => {
    initGA();
  }, []);

  // Track page views and stop detection when navigating away from home page (except search)
  useEffect(() => {
    // Track page view
    trackPageView(location.pathname);

    // Stop detection when navigating away from home page, except for search page
    if (
      location.pathname !== '/' &&
      location.pathname !== '/search' &&
      isDetecting
    ) {
      handleStopDetection();
      setIsDetecting(false);
    }
  }, [location.pathname, isDetecting]);

  return (
    <ErrorBoundary>
      <DetectionProvider
        startDetection={handleStartDetection}
        stopDetection={handleStopDetection}
        isDetecting={isDetecting}
      >
        <div className="min-h-screen bg-flamingo-50 dark:bg-neutral-900">
          {/* Floating Settings Overlay */}
          <SettingsOverlay onClick={() => setIsSidebarOpen(!isSidebarOpen)} />

          {/* Main Content */}
          <main className="pb-20">
            <Suspense
              fallback={
                <div className="flex h-screen items-center justify-center">
                  <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-sage-600" />
                </div>
              }
            >
              <Routes>
                <Route
                  path="/"
                  element={
                    <MinimalHome
                      ref={homeRef}
                      setIsDetecting={setIsDetecting}
                    />
                  }
                />
                <Route path="/search" element={<MinimalSearch />} />
                <Route path="/library" element={<MinimalLibrary />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/settings" element={<Settings />} />
              </Routes>
            </Suspense>
          </main>

          {/* Bottom Navigation */}
          <BottomNav />

          {/* Right Sidebar */}
          <RightSidebar
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
          />
        </div>
      </DetectionProvider>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
